// The replay's own clock, DOM- and render-free: which sim tick is on screen, whether it runs and how fast.
// A replay has frames from `firstTick` to `lastTick`; the playhead is a fractional tick between them, so the
// view can interpolate. Every jump that is not plain playback (a seek, a restart) raises a discontinuity the
// view consumes once — it must re-read the world there instead of firing the events it skipped over.

export const SPEEDS = [0.5, 1, 2, 4] as const;
export type Speed = (typeof SPEEDS)[number];

export type Playback = ReturnType<typeof createPlayback>;

/** `dt`: seconds per sim tick (the replay header's). */
export function createPlayback(firstTick: number, lastTick: number, dt: number) {
  let tick = firstTick, playing = true, speed: Speed = 1, jumped = true;
  const clamp = (t: number) => Math.max(firstTick, Math.min(lastTick, t));
  const seek = (t: number) => { tick = clamp(t); jumped = true; };

  return {
    get tick() { return tick; },
    get playing() { return playing; },
    get speed() { return speed; },
    get atEnd() { return tick >= lastTick; },
    get firstTick() { return firstTick; },
    get lastTick() { return lastTick; },
    /** Seconds of match time from the first frame to the playhead / to the last frame. */
    get timeSec() { return (tick - firstTick) * dt; },
    get totalSec() { return (lastTick - firstTick) * dt; },
    /** Moves the playhead by `realDt` wall seconds at the current speed; stops at the end. Returns the match
        seconds actually played (0 while paused), which is what the animation clock advances by. */
    advance(realDt: number): number {
      if (!playing || realDt <= 0) return 0;
      const from = tick;
      tick = clamp(tick + (realDt * speed) / dt);
      if (tick >= lastTick) playing = false;
      return (tick - from) * dt;
    },
    play() {
      if (tick >= lastTick) seek(firstTick);
      playing = true;
    },
    pause() { playing = false; },
    toggle() { if (playing) this.pause(); else this.play(); },
    setSpeed(s: Speed) { speed = s; },
    seek,
    seekSec(sec: number) { seek(firstTick + sec / dt); },
    seekBy(sec: number) { seek(tick + sec / dt); },
    /** True once after every jump (and at the start): the view must rebuild its state from scratch. */
    takeJump(): boolean { const j = jumped; jumped = false; return j; },
  };
}

export const mmss = (s: number) => {
  const whole = Math.max(0, Math.floor(s));
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, '0')}`;
};
